/**
 * Hook mínimo de lectura de la API. Ejecuta `fn` al montar (y cuando cambian
 * las `deps`), expone {data, error, loading, reload}. Evitamos TanStack Query:
 * la app es liviana y así los tests no necesitan mocks de librería.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client.js";

interface QueryState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function mensajeDeError(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Ocurrió un error";
}

export function useQuery<T>(fn: () => Promise<T>, deps: readonly unknown[] = []): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError(null);
    fnRef
      .current()
      .then((d) => {
        if (vivo) setData(d);
      })
      .catch((e: unknown) => {
        if (vivo) setError(mensajeDeError(e));
      })
      .finally(() => {
        if (vivo) setLoading(false);
      });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}
